import React, { useCallback, useEffect, useState } from "react";
import { Search, Loader2, ChevronLeft, ChevronRight, ExternalLink, Users } from "lucide-react";
import { getAllAdmissionsAPI } from "../services/apicollection";

const PANEL_STUDENT_URL = "https://panel.mindcoders.in/students";
const DEFAULT_LIMIT = 10;

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function StudentMangement() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const controller = new AbortController();

  const fetchAdmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAllAdmissionsAPI({ limit, page, search: appliedSearch || "", signal: controller.signal });
      if (res?.status === false) {
        setError(res.message || "Failed to load students");
        setStudents([]);
        setTotalRecords(0);
        setTotalPages(0);
        return;
      }
      const studentsData = res?.students;
      const records = Array.isArray(studentsData?.students) ? studentsData.students : [];
      const total = studentsData?.totalStudents ?? 0;

      setStudents(records);
      setTotalRecords(total);
      setTotalPages(Math.max(1, Math.ceil(total / limit)));
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load students");
      setStudents([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [limit, page, appliedSearch]);

  useEffect(() => {
    fetchAdmissions();
    return () => {
      controller.abort();
    };
  }, [fetchAdmissions]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const filteredStudents = students.filter((s) => {
    const term = (appliedSearch || "").toLowerCase();
    if (!term) return true;
    const name = (s?.studentName || s?.name || "").toLowerCase();
    const email = (s?.email || "").toLowerCase();
    const phone = (s?.number || s?.phone || s?.mobile || "").toString();
    const course = (s?.courseName || s?.course || "").toLowerCase();
    const studentId = (s?.studentId || "").toLowerCase();
    return (
      name.includes(term) ||
      email.includes(term) ||
      phone.includes(term) ||
      course.includes(term) ||
      studentId.includes(term)
    );
  });

  const displayList = appliedSearch ? filteredStudents : students;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Student Management</h2>
      </div>

      <section className="space-y-4">
        <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <input
              type="text"
              placeholder="Search by ID, name, email, phone, course..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-lg border border-[#1d3350] py-2 pl-9 pr-3 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              aria-label="Search students"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
          >
            <Search size={16} />
            Search
          </button>
        </form>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-16">
            <Loader2 size={28} className="animate-spin text-slate-400" />
          </div>
        ) : displayList.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-16 text-center">
            <Users size={40} className="text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">
              {appliedSearch ? "No students match your search." : "No students found."}
            </p>
          </div>
        ) : (
          <>
            <div className="relative overflow-hidden rounded-2xl bg-[#0f1c2e] shadow-[0_20px_50px_rgba(0,0,0,0.08)]">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-[#0d1b2e] border-b border-[#1d3350]">
                    <tr>
                      <th className="px-6 py-4 text-xs font-bold  first-letter:uppercase tracking-wide text-slate-500">
                        Student ID
                      </th>
                      <th className="px-6 py-4 text-xs font-bold  first-letter:uppercase tracking-wide text-slate-500">
                        Name
                      </th>
                      <th className="px-6 py-4 text-xs font-bold  first-letter:uppercase tracking-wide text-slate-500">
                        Email
                      </th>
                      <th className="px-6 py-4 text-xs font-bold  first-letter:uppercase tracking-wide text-slate-500">
                        Phone
                      </th>
                      <th className="px-6 py-4 text-xs font-bold  first-letter:uppercase tracking-wide text-slate-500">
                        Course
                      </th>
                      <th className="px-6 py-4 text-xs font-bold  first-letter:uppercase tracking-wide text-slate-500">
                        Admission Date
                      </th>
                      <th className="px-6 py-4 text-xs font-bold  first-letter:uppercase tracking-wide text-slate-500 text-right">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {displayList.map((student) => (
                      <tr key={student._id} className="transition-colors hover:bg-[#0d1b2e]">
                        <td className="px-6 py-4 text-sm font-medium text-slate-300 whitespace-nowrap">
                          {student.studentId || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300">
                          {student.studentName || student.name || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300">
                          {student.email || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                          {student.number ?? student.phone ?? student.mobile ?? "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300">
                          {student.courseName || student.course || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                          {formatDate(student.admissionDate || student.createdAt || student.date)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <a
                            href={`${PANEL_STUDENT_URL}/${student._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-slate-700"
                          >
                            <ExternalLink size={14} />
                            View details
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-[#1d3350] bg-[#0f1c2e] px-4 py-3">
                <p className="text-sm text-slate-400">
                  Showing page {page} of {totalPages}
                  {totalRecords > 0 && ` (${totalRecords} total)`}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#1d3350] px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-[#0d1b2e] disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={18} />
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#1d3350] px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-[#0d1b2e] disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Next page"
                  >
                    Next
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

export default StudentMangement;
